export class UnauthenticatedError extends Error {
  constructor() {
    super('Authentication is required.');
    this.name = 'UnauthenticatedError';
  }
}

export async function getJson<T>(path: string): Promise<T> {
  const response = await fetch(path, {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  });

  if (response.status === 401) throw new UnauthenticatedError();
  if (!response.ok) throw new Error(`${path} returned HTTP ${response.status}.`);
  return (await response.json()) as T;
}

export async function postJson<T>(path: string, body?: unknown): Promise<T> {
  const response = await fetch(path, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });

  if (response.status === 401) throw new UnauthenticatedError();
  if (!response.ok) {
    const payload = (await response.json().catch(() => ({}))) as { message?: string };
    throw new Error(payload.message ?? `${path} returned HTTP ${response.status}.`);
  }
  return (await response.json().catch(() => ({}))) as T;
}

export type UiHealth = 'online' | 'degraded' | 'offline' | 'disabled' | 'unknown';

export type HeaderSnapshot = {
  streaming: boolean;
  scene: string | null;
  nowPlaying: string | null;
  wamCategory: string | null;
};

export type ControlRoomSnapshot = {
  system: {
    status: UiHealth;
    database: UiHealth;
    version: string;
  };
  production: {
    agentConnected: boolean;
    agentVersion: string | null;
    obsStatus: UiHealth;
    scene: string | null;
    streaming: boolean;
    recording: boolean;
    soundboardStatus: UiHealth;
    soundboardClients: number;
    nowPlaying: string | null;
  };
  twitch: {
    live: boolean;
    viewers: number | null;
    subscribers: number | null;
    followers: number | null;
    title: string | null;
    category: string | null;
    streamStartedAt: string | null;
  };
  wam: {
    status: UiHealth;
    agentConnected: boolean;
    category: string | null;
  };
  bot: {
    status: UiHealth;
    recentChat: Array<{
      id: string;
      userName: string;
      text: string;
      sentAt: string;
    }>;
  };
};

type SystemSummaryResponse = {
  health?: { status?: string; database?: string };
  platform?: { version?: string };
};

type ProductionResponse = {
  activeAgent?: { connected?: boolean; agentVersion?: string | null } | null;
  obs?: {
    status?: string;
    currentProgramSceneName?: string | null;
    streaming?: boolean;
    recording?: boolean;
  };
  soundboard?: { status?: string; overlayClients?: number };
  textOutputs?: { nowPlaying?: string | null };
};

type TextOutputsResponse = {
  twitch?: {
    live?: boolean;
    currentViewers?: number | null;
    currentSubTotal?: number | null;
    followerTotal?: number | null;
    streamTitle?: string | null;
    gameName?: string | null;
    streamStartedAt?: string | null;
  };
};

type WamResponse = {
  agentConnected?: boolean;
  service?: {
    status?: string;
    current?: { category?: string | null } | null;
  };
};

type TwitchBotResponse = {
  enabled?: boolean;
  configured?: boolean;
  connected?: boolean;
  scopesReady?: boolean;
  recentChat?: Array<{ id: string; userName: string; text: string; sentAt: string }>;
};

function health(value: unknown): UiHealth {
  if (value === 'online') return 'online';
  if (value === 'disabled') return 'disabled';
  if (value === 'offline') return 'offline';
  if (value === 'degraded' || value === 'starting' || value === 'recovering' || value === 'connecting') {
    return 'degraded';
  }
  return 'unknown';
}

export function headerFromControlRoom(snapshot: ControlRoomSnapshot): HeaderSnapshot {
  return {
    streaming: snapshot.production.streaming,
    scene: snapshot.production.scene,
    nowPlaying: snapshot.production.nowPlaying,
    wamCategory: snapshot.wam.category,
  };
}

export async function loadHeaderState(): Promise<HeaderSnapshot> {
  const [production, wam] = await Promise.all([
    getJson<ProductionResponse>('/api/v1/production'),
    getJson<WamResponse>('/api/v1/wam'),
  ]);

  return {
    streaming: production.obs?.streaming === true,
    scene: production.obs?.currentProgramSceneName ?? null,
    nowPlaying: production.textOutputs?.nowPlaying ?? null,
    wamCategory: wam.service?.current?.category ?? null,
  };
}

export async function loadControlRoom(): Promise<ControlRoomSnapshot> {
  const [system, production, textOutputs, wam, bot] = await Promise.all([
    getJson<SystemSummaryResponse>('/api/v1/system/summary'),
    getJson<ProductionResponse>('/api/v1/production'),
    getJson<TextOutputsResponse>('/api/v1/text-outputs'),
    getJson<WamResponse>('/api/v1/wam'),
    getJson<TwitchBotResponse>('/api/v1/twitch-bot'),
  ]);

  const botStatus: UiHealth = !bot.enabled
    ? 'disabled'
    : bot.connected && bot.scopesReady
      ? 'online'
      : bot.configured
        ? 'degraded'
        : 'offline';

  return {
    system: {
      status: health(system.health?.status),
      database: health(system.health?.database),
      version: system.platform?.version ?? 'Unknown',
    },
    production: {
      agentConnected: production.activeAgent?.connected === true,
      agentVersion: production.activeAgent?.agentVersion ?? null,
      obsStatus: health(production.obs?.status),
      scene: production.obs?.currentProgramSceneName ?? null,
      streaming: production.obs?.streaming === true,
      recording: production.obs?.recording === true,
      soundboardStatus: health(production.soundboard?.status),
      soundboardClients: production.soundboard?.overlayClients ?? 0,
      nowPlaying: production.textOutputs?.nowPlaying ?? null,
    },
    twitch: {
      live: textOutputs.twitch?.live === true,
      viewers: textOutputs.twitch?.currentViewers ?? null,
      subscribers: textOutputs.twitch?.currentSubTotal ?? null,
      followers: textOutputs.twitch?.followerTotal ?? null,
      title: textOutputs.twitch?.streamTitle ?? null,
      category: textOutputs.twitch?.gameName ?? null,
      streamStartedAt: textOutputs.twitch?.streamStartedAt ?? null,
    },
    wam: {
      status: health(wam.service?.status),
      agentConnected: wam.agentConnected === true,
      category: wam.service?.current?.category ?? null,
    },
    bot: {
      status: botStatus,
      recentChat: bot.recentChat ?? [],
    },
  };
}
